import { useEffect, useRef, useState } from 'react'

import useSWR from 'swr'

import { trackAction } from '@features/my-gousto-page'
import {
  DASHBOARD_EXPERIENCE_STATE,
  fetchDashboardExperience,
} from '@library/api-customer-engagement-experience'
import type { DashboardSection } from '@library/api-customer-engagement-experience'

export const UNSUBSCRIBED_DASHBOARD_SWR_KEY = 'unsubscribed-dashboard'
// Most unsubscribed customers get a 204 and see the control view, so a hung dashboard request must
// not hold up My Gousto for all of them. SWR has no timeout of its own. The xAPI's upstream call
// times out at 3s, so this sits above that to avoid cutting off a slow but successful response.
export const UNSUBSCRIBED_DASHBOARD_LOADING_TIMEOUT_MS = 5000

type UseUnsubscribedDashboardResult = {
  isLoading: boolean
  sections: DashboardSection[] | null
  // Server-declared empty-state dashboard, read from the `state` field so gating never touches
  // analytics data.
  isEmptyState: boolean
}

export function useUnsubscribedDashboard(): UseUnsubscribedDashboardResult {
  const { data, error } = useSWR(UNSUBSCRIBED_DASHBOARD_SWR_KEY, fetchDashboardExperience)

  const sections = data?.status === 200 ? data.data?.sections ?? null : null
  const isEmptyState =
    data?.status === 200 && data.data?.state === DASHBOARD_EXPERIENCE_STATE.noOrder

  // Stop reporting loading after a cap so a hung request falls back to the control view. SWR keeps
  // fetching, so the dashboard still swaps in if it arrives afterwards.
  const [hasLoadingTimedOut, setHasLoadingTimedOut] = useState(false)
  useEffect(() => {
    const timeout = setTimeout(
      () => setHasLoadingTimedOut(true),
      UNSUBSCRIBED_DASHBOARD_LOADING_TIMEOUT_MS,
    )

    return () => clearTimeout(timeout)
  }, [])

  // On a fetch error (e.g. 5XX) we also stop reporting loading so the page falls back to
  // the control view rather than spinning forever.
  const isLoading = data === undefined && !error && !hasLoadingTimedOut

  const pageLoad = data?.status === 200 ? data.pageLoad : undefined

  // Fire the server-supplied page-load event once per mount. SWR revalidates after
  // skip/save mutations, so the ref guard stops the event re-firing on every refetch.
  const hasTrackedPageLoad = useRef(false)
  useEffect(() => {
    if (pageLoad && !hasTrackedPageLoad.current) {
      hasTrackedPageLoad.current = true
      trackAction(pageLoad)
    }
  }, [pageLoad])

  return { isLoading, sections, isEmptyState }
}
